import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { advisorMembers, type AdvisorMember } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

/**
 * Pessoas da assessoria (P-ACE-2): quem entra pela tela de acesso da assessoria e atua
 * por uma das corretoras dela.
 *
 * A pessoa ja precisa existir na empresa, convidada com perfil da corretora e sem
 * corretora propria. O servidor recusa quem e de uma corretora, quem tem perfil da
 * seguradora (a assessoria entra sempre com escopo de corretora, e a pessoa perderia
 * o acesso interno) e quem esta em outra assessoria. A marca de master e a do OnPoint
 * (IsAssessoriaMaster) e nao libera nada: o efeito nao tem fonte.
 */
export function AdvisorMembersSection({
  advisorId,
  podeEditar,
  ativa,
}: {
  advisorId: string;
  podeEditar: boolean;
  ativa: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [escolhido, setEscolhido] = useState('');
  const [master, setMaster] = useState(false);
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');

  const pessoas = useQuery({
    queryKey: ['advisor', advisorId, 'members'],
    queryFn: ({ signal }) => advisorMembers.list(advisorId, signal),
    enabled: advisorId !== '',
  });
  // Candidatas: a busca e no servidor, com os filtros da ligacao (sem corretora, sem
  // assessoria e so com perfis da corretora). Antes a tela pegava as 100 primeiras
  // pessoas da empresa e filtrava aqui: quem estava alem nao podia ser ligado, e quem
  // estava em outra assessoria aparecia para ser recusado (R5-B22). O servidor confere
  // tudo de novo na ligacao.
  const candidatas = useQuery({
    queryKey: ['advisor', advisorId, 'member-candidates', termo],
    queryFn: ({ signal }) => advisorMembers.candidates(advisorId, { q: termo }, signal),
    enabled: podeEditar && ativa && advisorId !== '',
  });
  const opcoes = (candidatas.data ?? []).map((pessoa) => ({
    value: pessoa.member_id,
    label: `${pessoa.name} (${pessoa.email})`,
  }));

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['advisor', advisorId, 'members'] });
    void queryClient.invalidateQueries({
      queryKey: ['advisor', advisorId, 'member-candidates'],
    });
  }

  const ligar = useMutation({
    mutationFn: (entrada: { memberId: string; master: boolean }) =>
      advisorMembers.link(advisorId, entrada.memberId, entrada.master),
    onSuccess: () => {
      toast.success('Pessoa ligada a assessoria.');
      setEscolhido('');
      setMaster(false);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const soltar = useMutation({
    mutationFn: (pessoa: AdvisorMember) =>
      advisorMembers.unlink(advisorId, pessoa.member_id),
    onSuccess: () => {
      toast.success('Pessoa retirada da assessoria.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <Card>
      <CardHeader
        title="Pessoas da assessoria"
        description="Quem entra pela tela de acesso da assessoria e escolhe a corretora pela qual vai atuar."
      />
      <CardBody className="flex flex-col gap-4">
        {pessoas.isError ? (
          <Alert tone="danger">{pessoas.error.message}</Alert>
        ) : (pessoas.data ?? []).length === 0 ? (
          <p className="text-sm text-ink-500">
            Nenhuma pessoa ligada a esta assessoria.
          </p>
        ) : (
          <ul className="divide-y divide-line" aria-label="Pessoas da assessoria">
            {(pessoas.data ?? []).map((pessoa) => (
              <li
                key={pessoa.member_id}
                className="flex flex-wrap items-center justify-between gap-2 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900">
                    {pessoa.name}{' '}
                    {pessoa.master ? <Badge tone="brand">Master</Badge> : null}
                  </p>
                  <p className="text-xs text-ink-500">{pessoa.email}</p>
                </div>
                {podeEditar ? (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        ligar.mutate({
                          memberId: pessoa.member_id,
                          master: !pessoa.master,
                        })
                      }
                    >
                      {pessoa.master ? 'Tirar master' : 'Marcar master'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => soltar.mutate(pessoa)}
                    >
                      Retirar
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {podeEditar && ativa ? (
          <div className="flex flex-wrap items-end gap-3 border-t border-line pt-4">
            <form
              className="flex min-w-[16rem] flex-1 items-end gap-2"
              onSubmit={(evento) => {
                evento.preventDefault();
                setEscolhido('');
                setTermo(busca.trim());
              }}
            >
              <div className="flex-1">
                <Field
                  label="Buscar pessoa"
                  value={busca}
                  maxLength={120}
                  placeholder="Nome ou e-mail"
                  onChange={(evento) => setBusca(evento.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary">
                Buscar
              </Button>
            </form>
            <div className="min-w-[16rem] flex-1">
              <Select
                label="Ligar pessoa"
                placeholder={
                  candidatas.isPending ? 'Carregando as pessoas' : 'Selecione a pessoa'
                }
                options={opcoes}
                value={escolhido}
                hint="So aparecem pessoas sem corretora, fora de outra assessoria e so com perfis da corretora."
                onChange={(evento) => setEscolhido(evento.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 pb-2 text-xs text-ink-700">
              <input
                type="checkbox"
                checked={master}
                onChange={(evento) => setMaster(evento.target.checked)}
              />
              Master da assessoria
            </label>
            <Button
              disabled={escolhido === ''}
              loading={ligar.isPending}
              onClick={() => ligar.mutate({ memberId: escolhido, master })}
            >
              Ligar
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
